/**
 * InViewportWrapper — IntersectionObserver-basiertes Lazy-Mount + Unmount.
 * -------------------------------------------------------------------------
 * Zweck: teure Kinder-Komponenten (z.B. WebGL-Viewer) nur mounten, wenn sie
 * im/nahe am Viewport sind, und unmounten wenn weit außerhalb — schützt vor
 * dem Chromium-WebGL-Context-Limit (8–16 pro Tab).
 *
 * Grid:
 *   ─ ENTER  (Kachel schneidet Viewport ± `rootMargin`)  → children mounten
 *   ─ IDLE   (im Viewport, aber User skrollt nicht)      → children bleiben
 *                                                          gemountet; parent
 *                                                          kann rAF pausieren
 *   ─ LEAVE  (aus Viewport ± `rootMargin`)                → onLeave (rAF
 *                                                          pausieren)
 *   ─ FAR    (Distanz > `unmountMargin` — Anzahl px vom
 *              Viewport-Rand)                             → children
 *                                                          unmounten
 *                                                          (fallback rendert)
 *
 * Zwei separate IntersectionObserver werden verwendet:
 *   1. „near": `rootMargin` — steuert onEnter/onLeave (rAF-Pause-Signal)
 *   2. „far":  `unmountMargin` — steuert das eigentliche Mount/Unmount
 *
 * Der doppelte Observer verhindert, dass ein Element ständig re-remounted
 * wird, wenn der User es knapp am Viewport-Rand vorbei scrollt — Unmount
 * greift erst deutlich später (Hysterese).
 */
import { useEffect, useRef, useState } from "react";

type Props = {
    children: React.ReactNode;
    /** Wird angezeigt, solange children NICHT gemountet sind. */
    fallback?: React.ReactNode;
    /**
     * Pause-Trigger — Puffer um den Viewport, ab dem `onEnter` feuert und
     * children als „nahe genug" gelten. rAF-Pause wird nach `onLeave`
     * empfohlen (der Consumer entscheidet). Default `200px`.
     */
    rootMargin?: string;
    /**
     * Unmount-Trigger — größerer Puffer. Solange die Kachel innerhalb dieses
     * Rings liegt, bleiben die children gemountet (auch off-viewport).
     * Erst darüber hinaus wird unmounted → Renderer disposed → GPU-Kontext
     * frei. Default `1200px` (großzügig, damit Standard-Scroll-Verhalten
     * keine ständigen Re-Mounts erzeugt).
     */
    unmountMargin?: string;
    /** Feuert wenn Kachel in den `rootMargin`-Puffer eintritt. */
    onEnter?: () => void;
    /** Feuert wenn Kachel den `rootMargin`-Puffer verlässt. */
    onLeave?: () => void;
    /** CSS-Klasse für den Root-Wrapper (default `contents`, damit kein
     *  Layout-Wrapper entsteht — Kachel-Frame kommt vom Consumer). */
    className?: string;
};

export default function InViewportWrapper({
    children,
    fallback = null,
    rootMargin = "200px",
    unmountMargin = "1200px",
    onEnter,
    onLeave,
    className,
}: Props) {
    const ref = useRef<HTMLDivElement | null>(null);
    const [mounted, setMounted] = useState(false);
    const nearRef = useRef(false);
    /** Grace-Period-Timer für Unmount. Auf echten Mobilgeräten dauert der
     *  Portrait↔Landscape-Reflow (inkl. iOS-Statusbar-Anim) bis ~800 ms.
     *  1000 ms Debounce überbrückt das sicher; ein neues `isIntersecting=true`
     *  innerhalb dieser Frist cancelt den Hide-Timer. */
    const hideTimeoutRef = useRef<number | null>(null);
    /** Fullscreen-Guard: wenn irgendein Descendant unseres Root-Elements
     *  gerade im Fullscreen-Modus ist, ist das Element aus dem viewport-flow
     *  gerissen — der IntersectionObserver meldet dann fälschlich
     *  `isIntersecting=false`. Wir dürfen in diesem Zustand NIEMALS unmounten,
     *  sonst zerlegt sich der Fullscreen-Viewer. */
    const isSelfFullscreenActive = () => {
        const fs = document.fullscreenElement;
        const el = ref.current;
        if (!fs || !el) return false;
        return fs === el || el.contains(fs) || (fs as Node).contains?.(el) === true;
    };

    useEffect(() => {
        const el = ref.current;
        if (!el || typeof IntersectionObserver === "undefined") {
            // Fallback für sehr alte Browser: sofort mounten.
            setMounted(true);
            return;
        }

        // Observer 1 · „near" — Pause/Resume-Signal
        const nearObs = new IntersectionObserver(
            (entries) => {
                for (const e of entries) {
                    if (e.isIntersecting && !nearRef.current) {
                        nearRef.current = true;
                        onEnter?.();
                    } else if (!e.isIntersecting && nearRef.current) {
                        nearRef.current = false;
                        onLeave?.();
                    }
                }
            },
            { rootMargin, threshold: 0 },
        );
        nearObs.observe(el);

        // Observer 2 · „far" — Mount/Unmount-Signal mit 1000 ms Grace-Period
        // und Fullscreen-Guard.
        //   · isIntersecting=true            → sofort mount, pending unmount canceln
        //   · isIntersecting=false + Fullscreen aktiv → NIEMALS unmounten
        //   · isIntersecting=false + normal   → 1000 ms Debounce, dann unmount
        // OrientationLock-Flag (Global-Context) skippt zusätzlich jedes Unmount
        // für 1 s nach `orientationchange` — echte Handys brauchen den Buffer.
        const farObs = new IntersectionObserver(
            (entries) => {
                for (const e of entries) {
                    if (e.isIntersecting) {
                        if (hideTimeoutRef.current !== null) {
                            window.clearTimeout(hideTimeoutRef.current);
                            hideTimeoutRef.current = null;
                        }
                        setMounted(true);
                    } else {
                        // Fullscreen-Guard: aktiver Fullscreen im Sub-Tree
                        // = Element aus Layout-Flow gerissen, IntersectionObserver
                        // meldet fälschlich false. Nicht anfassen.
                        if (isSelfFullscreenActive()) continue;
                        // OrientationLock-Guard: globaler Flag aus App-Level.
                        const w = window as unknown as { __hf_orientation_locked__?: boolean };
                        if (w.__hf_orientation_locked__) continue;
                        if (hideTimeoutRef.current !== null) {
                            window.clearTimeout(hideTimeoutRef.current);
                        }
                        hideTimeoutRef.current = window.setTimeout(() => {
                            hideTimeoutRef.current = null;
                            // Re-check innerhalb des Timers — falls in der
                            // Zwischenzeit Fullscreen/OrientationLock aktiv
                            // wurde, doch nicht unmounten.
                            if (isSelfFullscreenActive()) return;
                            const w2 = window as unknown as { __hf_orientation_locked__?: boolean };
                            if (w2.__hf_orientation_locked__) return;
                            setMounted(false);
                        }, 1000);
                    }
                }
            },
            { rootMargin: unmountMargin, threshold: 0 },
        );
        farObs.observe(el);

        return () => {
            nearObs.disconnect();
            farObs.disconnect();
            if (hideTimeoutRef.current !== null) {
                window.clearTimeout(hideTimeoutRef.current);
                hideTimeoutRef.current = null;
            }
        };
        // onEnter/onLeave sind stable Consumer-Callbacks; auslassen um
        // ständige Observer-Rebinds zu verhindern.
    }, [rootMargin, unmountMargin, onEnter, onLeave]);

    return (
        <div ref={ref} className={className}>
            {mounted ? children : fallback}
        </div>
    );
}
